import React, { useEffect, useState } from "react";
import { Button, Card, Descriptions, Image, Input, Modal, Space, Spin, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  HiArrowLeft,
  HiCheck,
  HiX,
  HiOutlineDownload,
  HiOutlineDocument,
  HiOutlineRefresh,
} from "react-icons/hi";
import {
  getFixer,
  clearFixer,
  updateFixerStatus,
  getFixerDocuments,
  getFixerAddresses,
  verifyFixerDocument,
  getFixerPayments,
} from "../../../store/slice/fixers";
import api from "../../../store/client";

function toAbsoluteUrl(url) {
  if (
    !url ||
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:")
  )
    return url;
  const base = (api.defaults.baseURL ?? "").replace(/\/$/, "");
  return `${base}/${url.replace(/^\//, "")}`;
}

// The Documents API returns fileUrl with a spurious "/api" segment
// (e.g. "/shared/api/documents/...") that 404s — the real route omits it.
function toDocumentUrl(fileUrl) {
  return toAbsoluteUrl(fileUrl?.replace(/^\/shared\/api\//, "/shared/"));
}

function AuthImage({ src, alt, width = 200, height = 140 }) {
  const [blobUrl, setBlobUrl] = React.useState(null);

  useEffect(() => {
    if (!src) return;
    let active = true;
    api.get(toDocumentUrl(src), { responseType: "blob" }).then((res) => {
      if (!active) return;
      setBlobUrl(URL.createObjectURL(res.data));
    });
    return () => {
      active = false;
    };
  }, [src]);

  if (!src) return <span className="text-gray-400">—</span>;
  if (!blobUrl) return <Spin size="small" />;
  return (
    <Image
      src={blobUrl}
      alt={alt}
      width={width}
      height={height}
      style={{ objectFit: "cover", borderRadius: 8 }}
    />
  );
}

const DOC_VERIFY_COLORS = { verified: "green", rejected: "red", pending: "gold" };
const DOC_VERIFY_LABELS = { verified: "Verified", rejected: "Rejected", pending: "Pending" };

function DocumentCard({ doc, userId }) {
  const dispatch = useDispatch();
  const verifyingDocumentType = useSelector((state) => state.fixers.verifyingDocumentType);
  const [downloading, setDownloading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [verifyComment, setVerifyComment] = useState("");
  const isImage = doc.contentType?.startsWith("image/");
  const isVerifying = verifyingDocumentType?.toLowerCase() === doc.documentType?.toLowerCase();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(toDocumentUrl(doc.fileUrl), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.originalFileName ?? doc.documentType;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const askVerify = (action) => {
    setPendingAction(action);
    setVerifyComment("");
    setModalOpen(true);
  };

  const handleVerifyOk = async () => {
    if (!verifyComment.trim()) return;
    const resultAction = await dispatch(
      verifyFixerDocument({
        userId,
        documentType: doc.documentType,
        status: pendingAction,
        comment: verifyComment.trim(),
      }),
    );
    setModalOpen(false);
    if (!verifyFixerDocument.fulfilled.match(resultAction)) {
      Modal.error({ content: resultAction.payload ?? "Failed to update document status." });
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      {isImage ? (
        <AuthImage src={doc.fileUrl} alt={doc.documentType} />
      ) : (
        <div
          className="flex flex-col items-center justify-center gap-1 bg-gray-50 rounded-lg cursor-pointer"
          style={{ width: 200, height: 140 }}
          onClick={handleDownload}
        >
          <HiOutlineDocument size="2.5em" className="text-gray-400" />
          <span className="text-xs text-gray-500 px-2 text-center truncate w-full">
            {doc.originalFileName}
          </span>
        </div>
      )}
      <div className="flex items-center gap-1">
        <span className="text-xs font-medium">{doc.documentType}</span>
        {doc.verificationStatus && (
          <Tag color={DOC_VERIFY_COLORS[doc.verificationStatus] ?? "default"} className="text-xs">
            {DOC_VERIFY_LABELS[doc.verificationStatus] ?? doc.verificationStatus}
          </Tag>
        )}
      </div>
      <Button
        size="small"
        type="link"
        icon={<HiOutlineDownload className="inline mr-1" />}
        loading={downloading}
        onClick={handleDownload}
      >
        Download
      </Button>
      <Space size="small">
        <Button
          size="small"
          icon={<HiCheck className="inline" />}
          loading={isVerifying}
          onClick={() => askVerify("verified")}
        >
          Verify
        </Button>
        <Button
          size="small"
          danger
          icon={<HiX className="inline" />}
          loading={isVerifying}
          onClick={() => askVerify("rejected")}
        >
          Reject
        </Button>
      </Space>

      <Modal
        title={pendingAction === "verified" ? "Verify Document" : "Reject Document"}
        open={modalOpen}
        okText={pendingAction === "verified" ? "Yes, Verify" : "Yes, Reject"}
        okType={pendingAction === "verified" ? "primary" : "danger"}
        okButtonProps={{ disabled: !verifyComment.trim() }}
        confirmLoading={isVerifying}
        onOk={handleVerifyOk}
        cancelText="Cancel"
        onCancel={() => setModalOpen(false)}
      >
        <p>Comment for {doc.documentType}:</p>
        <Input.TextArea
          rows={3}
          value={verifyComment}
          onChange={(e) => setVerifyComment(e.target.value)}
          placeholder="Enter comment"
        />
      </Modal>
    </div>
  );
}

const APPROVAL_ITEMS = [
  { key: "mobileVerified", label: "Mobile number", kind: "text" },
  { key: "aadhaarVerified", label: "Aadhaar", kind: "document", documentType: "Aadhaar", required: true },
  { key: "photoMatched", label: "Photo Matched", kind: "document", documentType: "Photo", required: true },
  { key: "panVerified", label: "PAN", kind: "document", documentType: "Pan", required: true },
  { key: "paymentVerified", label: "Payment", kind: "text" },
  { key: "epicVerified", label: "Epic", kind: "text" },
];

const STATUS_COLORS = {
  under_review: "gold",
  approved: "green",
  rejected: "red",
  reupload_required: "orange",
};

const STATUS_MODAL_TITLES = {
  approved: "Approve Fixer",
  rejected: "Reject Fixer",
  reupload_required: "Send for Reupload",
};
const STATUS_MODAL_OK_TEXT = {
  approved: "Yes, Approve",
  rejected: "Yes, Reject",
  reupload_required: "Yes, Send for Reupload",
};
const STATUS_MODAL_OK_TYPE = {
  approved: "primary",
  rejected: "danger",
  reupload_required: "primary",
};

const statusLabel = (status) =>
  (status ?? "")
    .split("_")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ") || "—";

const empty = <span className="text-gray-400">—</span>;
const val = (v) => v || empty;

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PAYMENT_STATUS_COLORS = {
  succeeded: "green",
  failed: "red",
  pending: "gold",
  created: "gold",
  expired: "default",
};

function formatAmount(amount, currency = "INR") {
  if (amount == null) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency }).format(amount);
}

function PaymentCard({ payment: p }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-medium">{p.invoiceNumber ?? `Payment #${p.id}`}</span>
        <Tag color={PAYMENT_STATUS_COLORS[p.status] ?? "default"}>{statusLabel(p.status)}</Tag>
      </div>
      <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
        <Descriptions.Item label="Subscription Tier">{val(p.subscriptionTier)}</Descriptions.Item>
        <Descriptions.Item label="Billing Cycle">{val(statusLabel(p.billingCycle))}</Descriptions.Item>
        <Descriptions.Item label="Service Category">{val(p.serviceCategory)}</Descriptions.Item>
        <Descriptions.Item label="Primary Service Area">{val(p.primaryServiceArea)}</Descriptions.Item>
        <Descriptions.Item label="Joining Fee">{val(formatAmount(p.joiningFee, p.currency))}</Descriptions.Item>
        <Descriptions.Item label="Subscription Amount">
          {val(formatAmount(p.subscriptionAmount, p.currency))}
        </Descriptions.Item>
        <Descriptions.Item label="GST">{val(formatAmount(p.gstAmount, p.currency))}</Descriptions.Item>
        <Descriptions.Item label="Total Amount">{val(formatAmount(p.amount, p.currency))}</Descriptions.Item>
        <Descriptions.Item label="Amount Paid">
          <span className="font-medium">{val(formatAmount(p.amountPaid, p.currency))}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Reference Number">{val(p.referenceNumber)}</Descriptions.Item>
        <Descriptions.Item label="Payment Id">{val(p.paymentId)}</Descriptions.Item>
        <Descriptions.Item label="Payment Session Id">{val(p.paymentSessionId)}</Descriptions.Item>
        <Descriptions.Item label="Created Date">{val(formatDateTime(p.createdDate))}</Descriptions.Item>
        <Descriptions.Item label="Paid Date">{val(formatDateTime(p.paidDate))}</Descriptions.Item>
        <Descriptions.Item label="Expires At">{val(formatDateTime(p.expiresAt))}</Descriptions.Item>
        {p.failureReason && (
          <Descriptions.Item label="Failure Reason" span={2}>
            <span className="text-red-500">{p.failureReason}</span>
          </Descriptions.Item>
        )}
      </Descriptions>
    </div>
  );
}

function FixerDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    fixer,
    documents,
    addresses,
    payments,
    paymentsTotalCollected,
    isLoading,
    isUpdatingStatus,
    isLoadingDocuments,
    isLoadingAddresses,
    isLoadingPayments,
  } = useSelector((state) => state.fixers);
  const [open, setOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    dispatch(clearFixer());
    dispatch(getFixer(id));
    dispatch(getFixerPayments(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (fixer?.userId) {
      dispatch(getFixerDocuments(fixer.userId));
      dispatch(getFixerAddresses(fixer.userId));
    }
  }, [fixer?.userId, dispatch]);

  if (isLoading || !fixer || String(fixer.id) !== id) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spin size="large" />
      </div>
    );
  }

  const f = fixer;
  const user = f.user ?? {};

  const findMatchedDoc = (documentType) =>
    documents.find((d) => d.documentType?.toLowerCase() === documentType.toLowerCase());

  const askConfirm = (status) => {
    setPendingStatus(status);
    setComment("");
    setOpen(true);
  };

  const requiredDocsVerified = APPROVAL_ITEMS.filter((it) => it.required).every(
    (it) => findMatchedDoc(it.documentType)?.verificationStatus === "verified",
  );
  const rejectedDocuments = documents.filter((d) => d.verificationStatus === "rejected");
  const canSubmit =
    pendingStatus === "approved" ? requiredDocsVerified : comment.trim().length > 0;

  const handleOk = async () => {
    if (!canSubmit) return;
    const payload =
      pendingStatus === "approved"
        ? { id: f.id, status: "approved", comment: comment.trim() }
        : { id: f.id, status: pendingStatus, comment: comment.trim() };
    const resultAction = await dispatch(updateFixerStatus(payload));
    setOpen(false);
    if (updateFixerStatus.fulfilled.match(resultAction)) {
      Modal.success({ content: resultAction.payload.message ?? "Status updated." });
      dispatch(getFixer(id));
    } else {
      Modal.error({ content: resultAction.payload ?? "Failed to update status." });
    }
  };

  const handleCancel = () => setOpen(false);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <Button
          icon={<HiArrowLeft className="inline mr-1" />}
          onClick={() => navigate("/fixers")}
        >
          Back
        </Button>
        <Space>
          <Tag color={STATUS_COLORS[f.status] ?? "default"} className="text-sm px-2 py-1">
            {statusLabel(f.status)}
          </Tag>
          {f.status === "under_review" && (
            <>
              <Button
                type="primary"
                icon={<HiCheck className="inline mr-1" />}
                loading={isUpdatingStatus}
                onClick={() => askConfirm("approved")}
              >
                Approve
              </Button>
              <Button
                danger
                icon={<HiX className="inline mr-1" />}
                loading={isUpdatingStatus}
                onClick={() => askConfirm("rejected")}
              >
                Reject
              </Button>
              {rejectedDocuments.length > 0 && (
                <Button
                  icon={<HiOutlineRefresh className="inline mr-1" />}
                  loading={isUpdatingStatus}
                  onClick={() => askConfirm("reupload_required")}
                >
                  Send for Reupload
                </Button>
              )}
            </>
          )}
        </Space>
      </div>

      {/* Personal Information */}
      <Card title="Personal Information">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Full Name">{val(user.fullName)}</Descriptions.Item>
          <Descriptions.Item label="Phone Number">{val(user.phoneNumber)}</Descriptions.Item>
          <Descriptions.Item label="WhatsApp Number">{val(f.whatsappNumber)}</Descriptions.Item>
          <Descriptions.Item label="Date of Birth">{val(formatDate(f.dob))}</Descriptions.Item>
          <Descriptions.Item label="Gender">{val(f.gender)}</Descriptions.Item>
          <Descriptions.Item label="Active">
            <Tag color={f.isActive ? "green" : "red"}>{f.isActive ? "Yes" : "No"}</Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Service Area */}
      <Card title="Service Area">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="State">{val(f.state)}</Descriptions.Item>
          <Descriptions.Item label="District">{val(f.district)}</Descriptions.Item>
          <Descriptions.Item label="Post Office">{val(f.postOffice)}</Descriptions.Item>
          <Descriptions.Item label="Police Station">{val(f.policeStation)}</Descriptions.Item>
          <Descriptions.Item label="City / Town / Village">{val(f.cityTownVillage)}</Descriptions.Item>
          <Descriptions.Item label="Primary Service Area">{val(f.primaryServiceArea)}</Descriptions.Item>
          {f.additionalServiceAreas?.length > 0 && (
            <Descriptions.Item label="Additional Service Areas" span={2}>
              <div className="flex flex-wrap gap-1">
                {f.additionalServiceAreas.map((p) => (
                  <Tag key={p}>{p}</Tag>
                ))}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Addresses */}
      <Card title="Addresses">
        {isLoadingAddresses ? (
          <Spin size="small" />
        ) : addresses.length === 0 ? (
          <span className="text-gray-400">No addresses on file.</span>
        ) : (
          <div className="flex flex-col gap-4">
            {addresses.map((addr) => (
              <div key={addr.id}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">{addr.label ?? addr.addressType}</span>
                  {addr.isDefault && <Tag color="blue">Default</Tag>}
                </div>
                <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                  <Descriptions.Item label="Address Line 1">{val(addr.addressLine1)}</Descriptions.Item>
                  <Descriptions.Item label="Address Line 2">{val(addr.addressLine2)}</Descriptions.Item>
                  <Descriptions.Item label="Landmark">{val(addr.landmark)}</Descriptions.Item>
                  <Descriptions.Item label="City / Town / Village">{val(addr.cityTownVillage)}</Descriptions.Item>
                  <Descriptions.Item label="Post Office">{val(addr.postOffice)}</Descriptions.Item>
                  <Descriptions.Item label="Police Station">{val(addr.policeStation)}</Descriptions.Item>
                  <Descriptions.Item label="District">{val(addr.district)}</Descriptions.Item>
                  <Descriptions.Item label="State">{val(addr.state)}</Descriptions.Item>
                  <Descriptions.Item label="Pincode">{val(addr.pincode)}</Descriptions.Item>
                </Descriptions>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Work Details */}
      <Card title="Work Details">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Service Categories" span={2}>
            <div className="flex flex-wrap gap-1">
              {(f.serviceCategories ?? []).map((c) => (
                <Tag key={c} color="blue">{c}</Tag>
              ))}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Worker Type">{val(f.workerType)}</Descriptions.Item>
          <Descriptions.Item label="Experience Tier">{val(f.experienceTier)}</Descriptions.Item>
          <Descriptions.Item label="Qualification">{val(f.qualification)}</Descriptions.Item>
          <Descriptions.Item label="Onboarding Source">{val(f.onboardingSource)}</Descriptions.Item>
          <Descriptions.Item label="Associate Id">{val(f.associateId)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Documents */}
      <Card title="Documents">
        {isLoadingDocuments ? (
          <Spin size="small" />
        ) : documents.length === 0 ? (
          <span className="text-gray-400">No documents uploaded.</span>
        ) : (
          <div className="flex flex-wrap gap-8">
            {documents.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} userId={f.userId} />
            ))}
          </div>
        )}
      </Card>

      {/* Payment */}
      <Card
        title="Payment Details"
        extra={
          payments.length > 0 && (
            <span className="text-sm">
              Total Collected:{" "}
              <span className="font-semibold">
                {formatAmount(paymentsTotalCollected, payments[0].currency)}
              </span>
            </span>
          )
        }
      >
        {isLoadingPayments ? (
          <Spin size="small" />
        ) : payments.length === 0 ? (
          <span className="text-gray-400">No payments on record.</span>
        ) : (
          <div className="flex flex-col gap-4">
            {payments.map((p) => (
              <PaymentCard key={p.id} payment={p} />
            ))}
          </div>
        )}
      </Card>

      {/* Audit */}
      <Card title="Audit">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Created By">{val(f.createdBy)}</Descriptions.Item>
          <Descriptions.Item label="Created Date">{val(formatDateTime(f.createdDate))}</Descriptions.Item>
          <Descriptions.Item label="Updated By">{val(f.updatedBy)}</Descriptions.Item>
          <Descriptions.Item label="Updated Date">{val(formatDateTime(f.updatedDate))}</Descriptions.Item>
          <Descriptions.Item label="Approved By">{val(f.approvedBy)}</Descriptions.Item>
          <Descriptions.Item label="Approved Date">{val(formatDateTime(f.approvedDate))}</Descriptions.Item>
          <Descriptions.Item label="Comment" span={2}>{val(f.comment)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        title={STATUS_MODAL_TITLES[pendingStatus]}
        open={open}
        okText={STATUS_MODAL_OK_TEXT[pendingStatus]}
        okType={STATUS_MODAL_OK_TYPE[pendingStatus]}
        onOk={handleOk}
        okButtonProps={{ disabled: !canSubmit }}
        confirmLoading={isUpdatingStatus}
        cancelText="Cancel"
        onCancel={handleCancel}
      >
        {pendingStatus === "approved" ? (
          <div className="flex flex-col gap-3">
            <p>Verification status for {user.fullName ?? "this fixer"}:</p>
            <div className="flex flex-col gap-2">
              {APPROVAL_ITEMS.map((it) => {
                if (it.kind === "text") {
                  return (
                    <div key={it.key} className="text-sm">
                      {it.label}
                    </div>
                  );
                }
                const matchedDoc = findMatchedDoc(it.documentType);
                const status = matchedDoc?.verificationStatus;
                const badgeColor =
                  status === "verified" ? "green" : status === "rejected" ? "red" : "default";
                const badgeText = matchedDoc
                  ? DOC_VERIFY_LABELS[status] ?? "Pending"
                  : "Not uploaded";
                return (
                  <div key={it.key} className="flex items-center gap-2 text-sm">
                    <span>{it.label}</span>
                    <Tag color={badgeColor}>{badgeText}</Tag>
                  </div>
                );
              })}
            </div>
            {!requiredDocsVerified && (
              <p className="text-red-500 text-xs">
                Aadhaar, Photo, and PAN must all be verified on their document cards before this
                fixer can be approved.
              </p>
            )}
            <div>
              <p className="mb-1">Comment (optional):</p>
              <Input.TextArea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add any additional remarks"
              />
            </div>
          </div>
        ) : pendingStatus === "reupload_required" ? (
          <div className="flex flex-col gap-2">
            <p>The following documents were rejected:</p>
            <div className="flex flex-wrap gap-1">
              {rejectedDocuments.map((d) => (
                <Tag key={d.id} color="red">{d.documentType}</Tag>
              ))}
            </div>
            <p className="mt-2">Instructions for {user.fullName ?? "this fixer"}:</p>
            <Input.TextArea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Explain what needs to be reuploaded"
            />
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p>
              Reason for rejecting {user.fullName ?? "this fixer"}:
            </p>
            <Input.TextArea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter rejection comment"
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default FixerDetails;
